import {
  Choice,
  CustomRequestOption,
  Upload,
  UploadState,
  isFile,
  FormControl,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useFieldArray} from 'react-hook-form';

import {isNil, omit} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import i18n from '@dms/i18n';
import {handleApiError, useTenant} from '@dms/shared';

import {DOCX_MIME_TYPE} from 'shared/web';

import {ConsentTypeFormTemplate, ConsentTypeFormType} from './ConsentTypeFormTemplate';

interface ConsentTypeFormTemplatesProps {
  control: FormControl<ConsentTypeFormType>;
}

export function ConsentTypeFormTemplates(props: ConsentTypeFormTemplatesProps) {
  const [uploadFile, {isLoading}] = useUploadFileMutation();
  const {isMultiLocale, supportedLocales, tenant} = useTenant();
  const [selectedLocale, setSelectedLocale] = useState<string | null>(
    tenant?.defaultLocale ?? null
  );
  const isUploadDisabled = isMultiLocale && isNil(selectedLocale);

  const {fields, remove, append, update} = useFieldArray({
    control: props.control,
    name: 'templates',
  });

  const handlePrimaryChange = (primaryIndex: number) => {
    fields.forEach((field, index) => {
      update(index, {...omit(['id'], field), isPrimary: primaryIndex === index});
    });
  };

  const handleUpload = async ({file}: CustomRequestOption) => {
    if (!isFile(file)) {
      return;
    }
    await uploadFile({file})
      .unwrap()
      .then((data) => {
        append({
          title: data.file.name,
          fileId: data.fileId,
          fileUri: data.fileUri,
          fileName: data.file.name,
          isPrimary: fields.length === 0,
          locale: isMultiLocale ? selectedLocale : null,
        });
      })
      .catch(handleApiError);
  };

  return (
    <VStack>
      {fields.map((field, index) => (
        <ConsentTypeFormTemplate
          key={field.id}
          index={index}
          control={props.control}
          onRemove={remove}
          template={omit(['id'], field)}
          onPrimaryChange={handlePrimaryChange}
          isMultiLocale={isMultiLocale}
        />
      ))}

      <VStack spacing={3}>
        <Text color="secondary" size="xSmall">
          {i18n.t('entity.gdpr.labels.consentType.uploadDocx')}
        </Text>
        <HStack spacing={4} align="flex-end">
          <Upload
            isMultiple
            type="button"
            accept={DOCX_MIME_TYPE}
            isDisabled={isUploadDisabled}
            uploadState={isLoading ? UploadState.Uploading : UploadState.Idle}
            errorIcon="navigation/cancel"
            uploadingIcon="action/hourglass_empty"
            uploadIcon="file/upload"
            data-testid="add-gdpr-consent-type-upload-button"
            customRequest={handleUpload}
          />
          <Show when={isMultiLocale}>
            <Box width={50}>
              <Choice
                label={i18n.t('general.labels.locales')}
                value={selectedLocale}
                options={supportedLocales}
                onChange={setSelectedLocale}
                data-testid="add-gdpr-consent-type-locale-select"
                isNotClearable
              />
            </Box>
          </Show>
        </HStack>
      </VStack>
    </VStack>
  );
}
